import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { IDatabaseService } from 'src/app/shared/services/contracts/database/database-service';
import { IGenericStorage } from 'src/app/shared/services/contracts/database/generic-storage';
import { DatabaseService } from 'src/app/shared/services/database.service';

import { IStateRegistryPersister } from '../contracts/state/state-registry-persister';
import { Deferred } from '../deferred/deferred';

@Injectable({ providedIn: 'root' })
export class StateRegistryPersister extends Subject<{ key: string; value: any }> implements IStateRegistryPersister {
  private _database: Partial<IGenericStorage>;
  private _ready = new Deferred<void>();

  constructor(@Inject(DatabaseService) private _dbService: IDatabaseService) {
    super();

    (async () => {
      this._database = await this._dbService.getDatabase('statePersister.db');
      this._ready.resolve();
    })();
  }

  public async update(state: string, path: string, models: any): Promise<void> {
    await this._ready.promise;
    await this._database.set(`${state}:${path}`, { _id: `${state}:${path}`, models });
  }

  public async rehydrate(): Promise<void> {
    const items = {};
    await this._ready.promise;

    const seenKeys = new Set<string>();

    // key is derived from item._id in sqlite storage - entries without _id will have undefined key even though the Key property is set
    await this._database.forEach((models, key) => {
      if (!key) {
        return;
      }
      seenKeys.add(key);
      // skip sentry throttle entries - they are not state entries
      // Entries created by SentryThrottleService start with 'sentry:'
      if (key.startsWith('sentry:')) {
        return;
      }
      // clear all entities with key :items during rehydrate to avoid uncontrolled state growth
      // dont clear patientAppUsers as they are only written to state once (during websync)
      if (key.endsWith(':items') && !key.includes('patientAppUser')) {
        this._database.set(key, { _id: key, models: [] });
      } else if (key.endsWith(':selectedItem')) {
        // Do not rehydrate selectedItem - it is tab-specific state and causes cross-tab
        // contamination when multiple tabs have different items selected (e.g. on Windows/Edge
        // where IndexedDB rehydration arrives after the subscription is set up, overwriting
        // the correct item loaded from the URL parameter with a stale item from another tab).
        this._database.set(key, { _id: key, models: null });
      } else {
        // Write everything else from state persister into state
        // Every custom keys like :dashboard, etc. must be handled and cleared on their own!
        items[key] = models;
      }
    });

    // Remove orphaned entries whose key was not seen during forEach.
    // On SQLite, forEach derives the key from item._id — entries without _id yield
    // undefined keys and are skipped above. This cleanup uses the actual DB keys to
    // find and delete those orphaned entries.
    // https://dev.azure.com/itlabsde/Konexi/_workitems/edit/23719/
    try {
      const allKeys = await this._database.keys();
      const orphanedKeys = allKeys.filter(k => !seenKeys.has(k));
      if (orphanedKeys.length) {
        await Promise.all(orphanedKeys.map(k => this._database.remove(k)));
      }
    } catch (e) {
      window.logger.error('[StateRegistryPersister] rehydrate: failed to clean up orphaned keys', e);
    }

    for (const key in items) {
      if (items.hasOwnProperty(key)) {
        this.next({ key, value: items[key].models });
      }
    }
    this.complete();
  }
}
